import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const LoginPage = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const { login } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        try {
            const client = await login({ email, password });
            if (client) {
                navigate('/client/qrcodes');
            } else {
                navigate('/admin/dashboard');
            }
        } catch (err) {
            console.error("Login fallito", err);
            setError("Credenziali non valide. Riprova.");
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col justify-center items-center p-4">
            <div className="w-full max-w-md relative z-10">

                {/* Logo e Titolo */}
                <div className="text-center mb-10 flex flex-col items-center">
                    {/* Sostituisci il path /logo.png con il percorso effettivo del tuo logo */}
                    <img
                        src="/logo.webp"
                        alt="QR Flow Logo"
                        className="h-32 w-auto mb-6 drop-shadow-lg"
                        onError={(e) => {
                            // Fallback se l'immagine non viene trovata
                            e.currentTarget.style.display = 'none';
                        }}
                    />
                    {/* Fallback Icona (mostrata se non c'è il logo o in attesa di inserirlo) */}
                    {!email && !password && (
                        <div className="hidden">
                            {/* Rimuovi "hidden" e nascondi l'img sopra se vuoi usare temporaneamente l'icona */}
                            <div className="inline-block p-4 rounded-2xl bg-gradient-to-br from-sky-500 to-blue-600 mb-4 shadow-lg shadow-sky-500/30">
                                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white"><rect width="6" height="6" x="3" y="3" rx="1"/><rect width="6" height="6" x="15" y="3" rx="1"/><rect width="6" height="6" x="3" y="15" rx="1"/><path d="M15 15h6v6h-6z"/></svg>
                            </div>
                        </div>
                    )}
                    <h1 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-emerald-400 tracking-tight">
                        Accedi a QrFlow
                    </h1>
                    <p className="text-slate-400 mt-2 font-medium">Inserisci le tue credenziali per continuare.</p>
                </div>

                {/* Modulo di Login (Glassmorphism effect) */}
                <div className="bg-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-8 shadow-2xl">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label htmlFor="email" className="block text-sm font-semibold text-slate-300 mb-2">Email</label>
                            <input
                                id="email"
                                type="email"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                placeholder="tu@email.com"
                                required
                                className="w-full bg-slate-900/50 border border-slate-600 rounded-xl px-4 py-3 text-white placeholder-slate-500 transition-all duration-300 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500"
                            />
                        </div>
                        <div>
                            <label htmlFor="password" className="block text-sm font-semibold text-slate-300 mb-2">Password</label>
                            <input
                                id="password"
                                type="password"
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                placeholder="••••••••"
                                required
                                className="w-full bg-slate-900/50 border border-slate-600 rounded-xl px-4 py-3 text-white placeholder-slate-500 transition-all duration-300 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500"
                            />
                        </div>

                        {/* Messaggio di Errore */}
                        {error && (
                            <div className="bg-red-500/10 border border-red-500/50 text-red-400 text-sm font-medium rounded-xl p-4 flex items-center gap-3">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                {error}
                            </div>
                        )}

                        <div className="pt-2">
                            <button
                                type="submit"
                                className="w-full bg-gradient-to-r from-sky-500 to-emerald-500 hover:from-sky-400 hover:to-emerald-400 text-white font-bold py-3 px-4 rounded-xl shadow-lg shadow-sky-500/25 transition-all duration-300 transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-sky-500"
                            >
                                Accedi
                            </button>
                        </div>
                    </form>

                    {/* Link Utili */}
                    <div className="text-center mt-6 text-sm">
                        <Link to="/recover-password" className="font-medium text-slate-400 hover:text-sky-400 transition-colors duration-300">
                            Hai dimenticato la password?
                        </Link>
                    </div>
                </div>

                {/* Footer Aziendale e Legale */}
                <div className="mt-12 text-center space-y-3">
                    <p className="text-sm text-slate-500 font-medium tracking-wide">
                        Created by{' '}
                        <a
                            href="https://axiomgroup.it"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sky-500 hover:text-sky-400 transition-colors duration-300 hover:underline decoration-sky-500/50 underline-offset-4"
                        >
                            AxiomGroup
                        </a>
                    </p>

                    {/* Link a Privacy Policy e Termini */}
                    <div className="flex justify-center items-center gap-3 text-xs text-slate-500">
                        <Link to="/privacy-policy" className="hover:text-sky-400 transition-colors duration-300">
                            Privacy Policy
                        </Link>
                        <span>&bull;</span>
                        <Link to="/terms-and-conditions" className="hover:text-sky-400 transition-colors duration-300">
                            Termini e Condizioni
                        </Link>
                    </div>
                </div>

            </div>

            {/* Effetti di sfondo decorativi (opzionali) */}
            <div className="fixed top-[-10%] left-[-10%] w-96 h-96 bg-sky-500/10 rounded-full blur-3xl pointer-events-none"></div>
            <div className="fixed bottom-[-10%] right-[-10%] w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none"></div>
        </div>
    );
};

export default LoginPage;